import {
  DENTITION_TYPE,
  DENTITION_TYPE_OPTIONS,
  TOOTH_ATTACHMENT,
  TOOTH_INFORMATION,
  TOOTH_MOVEMENT_RESTRICTION
} from 'constants/prescriptionOptions';
import { REFINEMENT_REASONS_OPTIONS } from 'constants/refinementCurrentStageOptions';
import { clinicalPreferencesRefinementType } from 'types/PendingPrescription.types';
import { Option } from 'types/select';
import { TPVFormTypes } from 'types/TPVForm.types';
import { TreatmentSpecifications } from 'types/TreatmentPlanning.types';
import {
  UltraRefinementCurrentStageType,
  UltraRefinementPrescriptionType,
  UltraRefinementTpvType
} from 'types/UltraRefinementPrescription.types';
import { ZenClearUltraApiInput } from 'types/ZenClearUltraApiInput.types';
import { formatTeeth } from 'utils/tpvUtils';
import { formatTeethsForController } from 'utils/TreatmentPlanningInstructions.utils';
import { isDentitionTypeSelected } from 'utils/ultraPrescription.shared';
import {
  splitTeethByDentition,
  splitTeethOrTextField
} from 'utils/ultraPrescription.teeth.utils';

const OTHER = 'Others';

export const isOtherOptionSelected = (selectedOptions?: Option[]) => {
  return selectedOptions?.find((option) => option?.value === OTHER);
};

const convertToOptionsType = (options: Option[], savedList?: string[]) => {
  return options.filter((option: Option) => savedList?.includes(option.value));
};

const formatAlignerInfo = (
  name: string,
  alignerInfo?: { name: string; value: string }[]
) => {
  return alignerInfo?.find((item) => item?.name === name)?.value || '';
};

const getToothChartValues = (
  toothList?: {
    position?: string;
    value?: string;
  }[]
) => {
  const toothChart: Record<string, string> = {};
  toothList?.forEach((item) => {
    if (item?.position && item?.value) {
      toothChart[item.position] = item.value.slice(0, -2);
    }
  });
  return toothChart;
};

export const hasUltraRefinementSubmissionReason = (
  tpv?: UltraRefinementTpvType
) => !!tpv?.ultraSubmissionReason?.length;

export const getUltraRefinementCurrentStageSavedValues = (
  tpv?: UltraRefinementTpvType
): UltraRefinementCurrentStageType => {
  return {
    refinementReason: {
      reason: convertToOptionsType(
        REFINEMENT_REASONS_OPTIONS,
        tpv?.ultraSubmissionReason
      ),
      otherText: tpv?.ultraSubmissionReasonText
    },
    alignerFitting: tpv?.ultraAlignerFitting,
    clinicalExamination: {
      upperSet: Number(formatAlignerInfo('upper', tpv?.ultraAlignerInfo)),
      lowerSet: Number(formatAlignerInfo('lower', tpv?.ultraAlignerInfo)),
      attachmentAdjustments: {
        attachment: tpv?.ultraAttachmentAdjustments,
        toothChart: formatTeethsForController(
          tpv?.ultraKeepSpecifiedAttachmentValues
        )
      },
      otherText: tpv?.ultraOtherExaminations
    }
  };
};

export const getUltraRefinementPrescriptionSavedValues = (
  tpv?: UltraRefinementTpvType,
  disablePrefill?: boolean
): UltraRefinementPrescriptionType => {
  const toothInfoMissingTeeth = tpv?.ultraToothInfo?.missingTeeth;
  const {
    adultTeeth: toothInfoAdultTeeth,
    primaryTeeth: legacyPrimaryInMissingTeeth
  } = splitTeethByDentition(toothInfoMissingTeeth);
  const toothInfoPrimaryField = splitTeethOrTextField(
    tpv?.ultraToothInfo?.primaryDentition
  );
  const toothInfoPrimaryTeeth =
    toothInfoPrimaryField.teeth || legacyPrimaryInMissingTeeth;
  const toothInfoDentitionValues = [
    ...(toothInfoAdultTeeth || !toothInfoMissingTeeth
      ? [DENTITION_TYPE.ADULT_DENTITION]
      : []),
    ...(toothInfoPrimaryTeeth ? [DENTITION_TYPE.PRIMARY_DENTITION] : [])
  ];
  const movementPrimaryField = splitTeethOrTextField(
    tpv?.ultraToothMovementRestrictions?.primaryDentition
  );
  const movementAdultTeeth = !disablePrefill
    ? tpv?.ultraToothMovementRestrictions?.restrictions
    : undefined;
  const movementPrimaryTeeth = movementPrimaryField.teeth;
  const movementDentitionValues = [
    ...(movementAdultTeeth ? [DENTITION_TYPE.ADULT_DENTITION] : []),
    ...(movementPrimaryTeeth ? [DENTITION_TYPE.PRIMARY_DENTITION] : []),
    ...(!movementAdultTeeth && !movementPrimaryTeeth
      ? [DENTITION_TYPE.ADULT_DENTITION]
      : [])
  ];
  const attachmentPrimaryField = splitTeethOrTextField(
    tpv?.ultraToothUnavailableForAttachment?.primaryDentition
  );
  const attachmentAdultTeeth = !disablePrefill
    ? tpv?.ultraToothUnavailableForAttachment?.noAttachmentValues
    : undefined;
  const attachmentPrimaryTeeth = attachmentPrimaryField.teeth;
  const attachmentDentitionValues = [
    ...(attachmentAdultTeeth ? [DENTITION_TYPE.ADULT_DENTITION] : []),
    ...(attachmentPrimaryTeeth ? [DENTITION_TYPE.PRIMARY_DENTITION] : []),
    ...(!attachmentAdultTeeth && !attachmentPrimaryTeeth
      ? [DENTITION_TYPE.ADULT_DENTITION]
      : [])
  ];

  return {
    toothInformation: {
      information: tpv?.ultraToothInfo?.value || '',
      dentitionSelection: convertToOptionsType(
        DENTITION_TYPE_OPTIONS,
        toothInfoDentitionValues
      ),
      toothChart: formatTeethsForController(toothInfoAdultTeeth),
      primaryToothChart: formatTeethsForController(toothInfoPrimaryTeeth)
    },
    dentitionSpace: {
      space: tpv?.ultraDentitionSpace?.value,
      toothChart: getToothChartValues(
        !disablePrefill ? tpv?.ultraDentitionSpace?.preferences : []
      )
    },
    toothMovementRestriction: {
      restriction: tpv?.ultraToothMovementRestrictions?.value || '',
      dentitionSelection: convertToOptionsType(
        DENTITION_TYPE_OPTIONS,
        movementDentitionValues
      ),
      toothChart: formatTeethsForController(movementAdultTeeth),
      primaryToothChart: formatTeethsForController(movementPrimaryTeeth)
    },
    toothAttachment: {
      attachment: tpv?.ultraToothUnavailableForAttachment?.value || '',
      dentitionSelection: convertToOptionsType(
        DENTITION_TYPE_OPTIONS,
        attachmentDentitionValues
      ),
      toothChart: formatTeethsForController(attachmentAdultTeeth),
      primaryToothChart: formatTeethsForController(attachmentPrimaryTeeth)
    },
    specialNotes: tpv?.specialNotes || '',
    translatedSpecialNotes: tpv?.translatedSpecialNotes || ''
  };
};

export const getUltraRefinementDefaultTreatmentSpec = (
  isSubmitted: boolean,
  treatmentSpecifications?: TreatmentSpecifications,
  hideLiveTranslation?: boolean
) => {
  const tpv = treatmentSpecifications?.tpv as
    | UltraRefinementTpvType
    | undefined;
  if (isSubmitted || hasUltraRefinementSubmissionReason(tpv)) {
    return {
      currentStage: getUltraRefinementCurrentStageSavedValues(tpv),
      treatmentSpecifications: {
        ZenClearUltra: getUltraRefinementPrescriptionSavedValues(tpv),
        treatmentType: 'ZenClearUltra'
      }
    };
  }

  return {
    currentStage: {
      refinementReason: { reason: [], otherText: '' },
      alignerFitting: '',
      clinicalExamination: {
        upperSet: undefined,
        lowerSet: undefined,
        attachmentAdjustments: { attachment: '', toothChart: undefined },
        otherText: ''
      }
    },
    treatmentSpecifications: {
      ZenClearUltra: {
        toothInformation: {
          information: '',
          dentitionSelection: [],
          toothChart: undefined,
          primaryToothChart: undefined
        },
        dentitionSpace: { space: '', toothChart: undefined },
        toothMovementRestriction: {
          restriction: '',
          dentitionSelection: [],
          toothChart: undefined,
          primaryToothChart: undefined
        },
        toothAttachment: {
          attachment: '',
          dentitionSelection: [],
          toothChart: undefined,
          primaryToothChart: undefined
        },
        specialNotes: treatmentSpecifications?.treatmentPlanInstructions || '',
        translatedSpecialNotes: hideLiveTranslation
          ? ''
          : tpv?.translatedSpecialNotes || ''
      },
      treatmentType: 'ZenClearUltra'
    }
  };
};

export const buildUltraRefinementTpvPayload = (
  currentStage?: UltraRefinementCurrentStageType,
  zenClearUltra?: UltraRefinementPrescriptionType,
  clinicalPrefs?: clinicalPreferencesRefinementType,
  totalAlignerSet?: number
): ZenClearUltraApiInput => {
  return {
    type: TPVFormTypes.ZENCLEAR_ULTRA,
    totalAlignerSet: String(totalAlignerSet || 0),
    ultraSubmissionReason:
      currentStage?.refinementReason?.reason?.map((item) => item?.value) || [],
    ultraSubmissionReasonText: currentStage?.refinementReason?.otherText || '',
    ultraAlignerFitting: currentStage?.alignerFitting,
    ultraAlignerInfo: [
      {
        name: 'upper',
        value: currentStage?.clinicalExamination?.upperSet?.toString() || ''
      },
      {
        name: 'lower',
        value: currentStage?.clinicalExamination?.lowerSet?.toString() || ''
      }
    ],
    ultraAttachmentAdjustments:
      currentStage?.clinicalExamination?.attachmentAdjustments?.attachment ||
      '',
    ultraKeepSpecifiedAttachmentValues:
      formatTeeth(
        currentStage?.clinicalExamination?.attachmentAdjustments?.toothChart
      ) || '',
    ultraOtherExaminations: currentStage?.clinicalExamination?.otherText || '',
    ...(!!zenClearUltra?.toothInformation?.information && {
      ultraToothInfo: {
        value: zenClearUltra?.toothInformation?.information,
        ...(zenClearUltra?.toothInformation?.information ===
          TOOTH_INFORMATION.MISSING_TEETH &&
          isDentitionTypeSelected(
            zenClearUltra?.toothInformation?.dentitionSelection,
            DENTITION_TYPE.ADULT_DENTITION
          ) && {
            missingTeeth: formatTeeth(
              zenClearUltra?.toothInformation?.toothChart
            )
          }),
        primaryDentition:
          (zenClearUltra?.toothInformation?.information ===
            TOOTH_INFORMATION.MISSING_TEETH &&
          isDentitionTypeSelected(
            zenClearUltra?.toothInformation?.dentitionSelection,
            DENTITION_TYPE.PRIMARY_DENTITION
          )
            ? formatTeeth(zenClearUltra?.toothInformation?.primaryToothChart)
            : '') || undefined
      }
    }),
    ...(!!zenClearUltra?.dentitionSpace?.space && {
      ultraDentitionSpace: {
        value: zenClearUltra?.dentitionSpace?.space,
        preferences:
          zenClearUltra?.dentitionSpace?.space ===
          'Have preference or specific instructions'
            ? Object.keys(zenClearUltra?.dentitionSpace?.toothChart || {})
                ?.filter(
                  (item) => zenClearUltra?.dentitionSpace?.toothChart?.[item]
                )
                ?.map((item) => ({
                  position: item,
                  value: `${zenClearUltra?.dentitionSpace?.toothChart?.[item]}mm`
                }))
            : undefined
      }
    }),
    ...(!!zenClearUltra?.toothMovementRestriction?.restriction && {
      ultraToothMovementRestrictions: {
        value: zenClearUltra?.toothMovementRestriction?.restriction,
        ...(zenClearUltra?.toothMovementRestriction?.restriction ===
          TOOTH_MOVEMENT_RESTRICTION.SELECT_TEETH_SHOULD_NOT_BE_MOVED && {
          ...(isDentitionTypeSelected(
            zenClearUltra?.toothMovementRestriction?.dentitionSelection,
            DENTITION_TYPE.ADULT_DENTITION
          ) && {
            restrictions: formatTeeth(
              zenClearUltra?.toothMovementRestriction?.toothChart
            )
          }),
          primaryDentition:
            (isDentitionTypeSelected(
              zenClearUltra?.toothMovementRestriction?.dentitionSelection,
              DENTITION_TYPE.PRIMARY_DENTITION
            )
              ? formatTeeth(
                  zenClearUltra?.toothMovementRestriction?.primaryToothChart
                )
              : '') || undefined
        })
      }
    }),
    ...(!!zenClearUltra?.toothAttachment?.attachment && {
      ultraToothUnavailableForAttachment: {
        value: zenClearUltra?.toothAttachment?.attachment,
        ...(zenClearUltra?.toothAttachment?.attachment ===
          TOOTH_ATTACHMENT.NO_ATTACHMENT && {
          ...(isDentitionTypeSelected(
            zenClearUltra?.toothAttachment?.dentitionSelection,
            DENTITION_TYPE.ADULT_DENTITION
          ) && {
            noAttachmentValues: formatTeeth(
              zenClearUltra?.toothAttachment?.toothChart
            )
          }),
          primaryDentition:
            (isDentitionTypeSelected(
              zenClearUltra?.toothAttachment?.dentitionSelection,
              DENTITION_TYPE.PRIMARY_DENTITION
            )
              ? formatTeeth(zenClearUltra?.toothAttachment?.primaryToothChart)
              : '') || undefined
        })
      }
    }),
    specialNotes: zenClearUltra?.specialNotes,
    translatedSpecialNotes: zenClearUltra?.translatedSpecialNotes,
    clinicalPreferences: clinicalPrefs?.ZenClearUltra?.others
      ? { others: clinicalPrefs.ZenClearUltra.others }
      : {}
  };
};
